import { isDevMode } from "@angular/core";
import { Subject } from "rxjs";
import type { Observable } from "rxjs";
import type { ZodType } from "zod";

/**
 * Details of an API payload that failed its Zod wire schema. Forwarded to the
 * registered {@link WireValidationReporter} so the host app can surface it
 * (e.g. to Bugsnag).
 */
export interface WireValidationFailure {
  /** Schema name, used to group failures (e.g. "Snippet"). */
  schemaName: string;
  /** Zod issues describing what did not match. */
  issues: readonly unknown[];
  /** The raw payload that failed, for diagnosis. */
  data: unknown;
}

export type WireValidationReporter = (failure: WireValidationFailure) => void;

let reporter: WireValidationReporter | null = null;

const failures = new Subject<WireValidationFailure>();

/**
 * Stream of wire-schema validation failures, emitted on every failure
 * (before the dev-mode rethrow) regardless of any registered reporter.
 * The app's UpdateService subscribes to decide whether the running bundle
 * is stale and should be refreshed (see update.service.ts).
 */
export const wireValidationFailures$: Observable<WireValidationFailure> =
  failures.asObservable();

/**
 * Register a reporter for wire-schema validation failures. The common library
 * has no Bugsnag dependency (serializers are plain singletons, not DI
 * services), so the host app wires its reporter in at bootstrap. Pass null to
 * clear. Console logging happens regardless of whether a reporter is set.
 */
export function setWireValidationReporter(
  fn: WireValidationReporter | null,
): void {
  reporter = fn;
}

/**
 * Validate an API payload against its wire schema.
 *
 * The breadcrumb name (used to group failures) is taken from `schemaName` when
 * given, else from the schema's own `.description` (set via `.describe("Name")`
 * on the wire schema, so callers don't have to pass it separately), else a
 * generic fallback.
 *
 * Policy (see design/zod-frontend-validation/PLAN.md):
 * - success: return the parsed value.
 * - failure: log to the console and forward to the registered reporter. In dev
 *   builds, rethrow so drift is loud during development; in production, return
 *   the raw payload so a benign additive server change never black-holes a
 *   page (mirrors the pre-Zod cast-and-hope behaviour).
 */
export function validateWire<T>(
  schema: ZodType<T>,
  data: unknown,
  schemaName?: string,
): T {
  const result = schema.safeParse(data);
  if (result.success) {
    return result.data;
  }

  const name = schemaName ?? schema.description ?? "wire";
  const issues = result.error.issues;
  console.error(`[wire] ${name} failed schema validation`, issues, data);
  const failure: WireValidationFailure = { schemaName: name, issues, data };
  reporter?.(failure);
  failures.next(failure);

  if (isDevMode()) {
    throw result.error;
  }

  return data as T;
}

/**
 * {@link validateWire} for the deliberate case where the consumed type differs
 * from the schema's generated wire type, e.g. a strict `*Wire` shape mapped onto
 * a hand interface (`SelfServMetaWireSchema` -> `ISelfServMeta`). Validates
 * against `schema`, then returns the value typed as `T`, keeping that
 * wire-to-consumed assertion in one named place instead of an opaque
 * `as unknown as T` at each call site.
 */
export function validateWireAs<T>(
  schema: ZodType,
  data: unknown,
  schemaName?: string,
): T {
  return validateWire(schema, data, schemaName) as T;
}
